import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  Bell,
  Calendar,
  HelpCircle,
  Home,
  LogOut,
  MessageCircle,
  Presentation,
  Search,
  User,
  Users
} from 'lucide-react';
import sytelineLogo from '../assets/syteline-usergroup.png';
import { Button } from './ui';
import { useAuth } from '../auth/AuthProvider';
import { subscribeDiscussionNotificationSettings, subscribeTopics } from '../lib/firestore';
import { isCommitteeAdmin } from '../lib/admin';
import {
  DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS,
  effectiveTopicSummaryMode,
  notificationLastViewedAtMs
} from '../lib/notifications';
import { formatPersonName } from '../lib/names';
import type { DiscussionNotificationSettings, DiscussionTopic } from '../lib/types';

type NavIcon = React.ElementType<{ className?: string; 'aria-hidden'?: React.AriaAttributes['aria-hidden'] }>;
type NavLink = ({ label: string; icon: NavIcon; hidden?: boolean } & ({ to: string } | { href: string }));

function UsersPresentationIcon(props: { className?: string; 'aria-hidden'?: React.AriaAttributes['aria-hidden'] }) {
  return (
    <span
      className={clsx('inline-flex h-5 w-11 items-center justify-center gap-0.5', props.className)}
      aria-hidden={props['aria-hidden']}
    >
      <Users className="h-5 w-5 shrink-0" />
      <Presentation className="h-5 w-5 shrink-0" />
    </span>
  );
}

const NAV: NavLink[] = [
  { href: 'https://sytelineusers.com/', label: 'Home', icon: Home },
  { to: '/discussions', label: 'Discussions', icon: MessageCircle },
  { to: '/events', label: 'Events', icon: Calendar, hidden: true },
  { to: '/conference', label: 'Conference', icon: UsersPresentationIcon },
  { to: '/help', label: 'Help & FAQ', icon: HelpCircle },
  // Hidden for now, retained in routing for later rollout.
  // { to: '/poll', label: 'Poll' },
  // { to: '/directory', label: 'Directory' }
];

const VISIBLE_NAV = NAV.filter((item) => !item.hidden);
const MOBILE_HOME_NAV = VISIBLE_NAV.filter((item) => 'href' in item && item.label === 'Home');
const MOBILE_HELP_NAV = VISIBLE_NAV.filter((item) => 'to' in item && item.to === '/help');
const MOBILE_PRIMARY_NAV = VISIBLE_NAV.filter(
  (item) => 'to' in item && (item.to === '/discussions' || item.to === '/conference')
);

type NavItemProps =
  | {
      label: string;
      active: boolean;
      icon?: NavIcon;
      to: string;
      href?: never;
      onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    }
  | {
      label: string;
      active: boolean;
      icon?: NavIcon;
      href: string;
      to?: never;
      onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    };

function NavItem(props: NavItemProps) {
  const className = clsx(
    'flex items-center whitespace-nowrap rounded-xl px-3 py-2 text-sm font-medium transition',
    props.active ? 'bg-zinc-900 text-white' : 'text-zinc-800 hover:bg-zinc-100'
  );

  if ('href' in props) {
    return (
      <a href={props.href} className={className} onClick={props.onClick}>
        {props.label}
      </a>
    );
  }

  return (
    <Link to={props.to} className={className} onClick={props.onClick}>
      {props.label}
    </Link>
  );
}

function MobileHeaderNavItem(props: NavItemProps & { onNavigate?: (event: React.MouseEvent<HTMLAnchorElement>) => void }) {
  const Icon = props.icon;
  const className = clsx(
    MOBILE_HEADER_ICON_CLASS,
    'lg:hidden',
    props.active ? 'bg-zinc-900 text-white hover:bg-zinc-900' : undefined
  );

  if ('href' in props) {
    return (
      <a href={props.href} className={className} onClick={props.onClick ?? props.onNavigate} title={props.label} aria-label={props.label}>
        {Icon ? <Icon className={MOBILE_ICON_SIZE} aria-hidden /> : null}
      </a>
    );
  }

  return (
    <Link to={props.to} className={className} onClick={props.onClick ?? props.onNavigate} title={props.label} aria-label={props.label}>
      {Icon ? <Icon className={MOBILE_ICON_SIZE} aria-hidden /> : null}
    </Link>
  );
}

function MobilePrimaryNavItem(props: NavItemProps & { onNavigate?: (event: React.MouseEvent<HTMLAnchorElement>) => void }) {
  const Icon = props.icon;
  const icon = Icon ? (
    <Icon className={props.label === 'Conference' ? undefined : 'h-5 w-5'} aria-hidden />
  ) : null;
  const className = clsx(
    'flex h-9 items-center justify-center rounded-xl border px-3 transition',
    props.active
      ? 'border-zinc-900 bg-zinc-900 text-white'
      : 'border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50'
  );

  if ('href' in props) {
    return (
      <a href={props.href} className={className} onClick={props.onClick ?? props.onNavigate} title={props.label} aria-label={props.label}>
        {icon}
      </a>
    );
  }

  return (
    <Link to={props.to} className={className} onClick={props.onClick ?? props.onNavigate} title={props.label} aria-label={props.label}>
      {icon}
    </Link>
  );
}

const MOBILE_ICON_SIZE = 'h-4 w-4';
const MOBILE_HEADER_ICON_CLASS =
  'inline-flex h-8 w-8 items-center justify-center rounded-xl text-zinc-900 transition hover:bg-zinc-100';

function isNavActive(pathname: string, item: NavLink) {
  if (!('to' in item)) return false;
  if (item.to === '/discussions') {
    return pathname === '/discussions' || pathname.startsWith('/discussion/');
  }
  return pathname === item.to || (item.to !== '/' && pathname.startsWith(item.to));
}

function HeaderSearchForm(props: {
  value: string;
  className?: string;
  onChange: (value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form onSubmit={props.onSubmit} className={clsx('relative', props.className)} role="search">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500"
        aria-hidden
      />
      <input
        type="search"
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        placeholder="Search"
        aria-label="Search discussions and conference sessions"
        className="h-9 w-full rounded-xl border border-zinc-300 bg-white pl-8 pr-3 text-sm text-zinc-900 outline-none transition placeholder:text-zinc-500 focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200"
      />
    </form>
  );
}

export function AppShell(props: { children: React.ReactNode }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const activePath = loc.pathname;
  const { user, logout } = useAuth();
  const adminVisible = isCommitteeAdmin(user);
  const signedInName = formatPersonName(user?.displayName) || 'Member';
  const notificationsActive = activePath === '/notifications' || activePath.startsWith('/notifications/');
  const headerIsSticky = !activePath.startsWith('/admin/prizes');
  const [notificationSettings, setNotificationSettings] = React.useState<DiscussionNotificationSettings>(
    DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS
  );
  const [notificationTopics, setNotificationTopics] = React.useState<DiscussionTopic[]>([]);
  const [searchValue, setSearchValue] = React.useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false);

  React.useEffect(() => {
    if (activePath === '/search') {
      setSearchValue(new URLSearchParams(loc.search).get('q') ?? '');
    }
  }, [activePath, loc.search]);

  React.useEffect(() => {
    if (!user?.sub) {
      setNotificationSettings(DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS);
      return;
    }
    return subscribeDiscussionNotificationSettings(user.sub, setNotificationSettings);
  }, [user?.sub]);

  React.useEffect(() => {
    if (!user?.sub) {
      setNotificationTopics([]);
      return;
    }
    return subscribeTopics({ onData: setNotificationTopics, take: 200 });
  }, [user?.sub]);

  const unreadNotificationCount = React.useMemo(() => {
    if (!user?.sub) return 0;

    const lastViewedAtMs = notificationLastViewedAtMs(notificationSettings);
    return notificationTopics.filter((topic) => {
      if (effectiveTopicSummaryMode(topic, notificationSettings) === 'off') return false;
      const topicActivityAt = Math.max(topic.updatedAt || 0, topic.lastActivityAt || 0, topic.createdAt || 0);
      return topicActivityAt > lastViewedAtMs;
    }).length;
  }, [notificationSettings, notificationTopics, user?.sub]);
  const hasUnreadNotifications = unreadNotificationCount > 0;

  const notificationButtonClass = notificationsActive
    ? 'bg-zinc-900 text-white hover:bg-zinc-900'
    : hasUnreadNotifications
    ? 'hover:bg-zinc-100'
    : undefined;
  const notificationBellClass = clsx(
    'h-5 w-5 md:h-5 md:w-5',
    notificationsActive ? 'text-white' : hasUnreadNotifications ? 'text-red-600' : undefined
  );
  const mobileNotificationBellClass = clsx(
    MOBILE_ICON_SIZE,
    notificationsActive ? 'text-white' : hasUnreadNotifications ? 'text-red-600' : undefined
  );

  function submitSearch(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = searchValue.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search');
  }

  function handleNavClick(item: NavLink) {
    return (event: React.MouseEvent<HTMLAnchorElement>) => {
      if ('to' in item && item.to === '/discussions' && activePath === '/discussions') {
        event.preventDefault();
        navigate('/discussions', { replace: true, state: { resetDiscussionsAt: Date.now() } });
      }
    };
  }

  return (
    <div className="min-h-screen bg-zinc-100 text-zinc-900">
      <header className={clsx('z-10 bg-zinc-100', headerIsSticky ? 'sticky top-0' : 'relative')}>
        <div className="mx-auto max-w-[1400px] px-1 pt-1 md:px-4 md:pt-4">
          <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
            <div className="flex items-center gap-2 px-3 py-3 lg:grid lg:grid-cols-[auto,1fr,auto] lg:gap-4 lg:px-4">
              <div className="flex items-center">
                <img
                  src={sytelineLogo}
                  alt="SyteLine User Group"
                  className="block h-auto w-full max-w-[104px] object-contain sm:max-w-[136px] lg:max-w-[220px]"
                  style={{ maxHeight: 56 }}
                />
              </div>
              <nav className="hidden min-w-0 items-center gap-1 overflow-x-auto lg:flex">
                {VISIBLE_NAV.map((n) => {
                  const isInternal = 'to' in n;
                  const active = isNavActive(activePath, n);

                  if (isInternal) {
                    return (
                      <NavItem
                        key={n.to}
                        to={n.to}
                        label={n.label}
                        icon={n.icon}
                        active={active}
                        onClick={handleNavClick(n)}
                      />
                    );
                  }

                  return <NavItem key={n.href} href={n.href} label={n.label} icon={n.icon} active={false} />;
                })}
                {adminVisible ? (
                  <NavItem
                    to="/admin"
                    label="Admin"
                    active={activePath === '/admin' || activePath.startsWith('/admin/')}
                  />
                ) : null}
              </nav>
              <div className="ml-auto flex items-center justify-end gap-1 lg:ml-0 lg:gap-2">
                <HeaderSearchForm
                  value={searchValue}
                  onChange={setSearchValue}
                  onSubmit={submitSearch}
                  className="hidden w-40 lg:block lg:w-56"
                />
                {MOBILE_HOME_NAV.map((n) => {
                  if ('href' in n) {
                    return (
                      <MobileHeaderNavItem
                        key={n.href}
                        href={n.href}
                        label={n.label}
                        icon={n.icon}
                        active={false}
                      />
                    );
                  }

                  return (
                    <MobileHeaderNavItem
                      key={n.to}
                      to={n.to}
                      label={n.label}
                      icon={n.icon}
                      active={false}
                    />
                  );
                })}
                <span title="Search" className="lg:hidden">
                  <button
                    type="button"
                    className={clsx(
                      MOBILE_HEADER_ICON_CLASS,
                      mobileSearchOpen ? 'bg-zinc-100 text-zinc-900 ring-1 ring-inset ring-zinc-300' : undefined
                    )}
                    aria-label={mobileSearchOpen ? 'Hide search' : 'Show search'}
                    aria-expanded={mobileSearchOpen}
                    aria-controls="mobile-header-search"
                    onClick={() => setMobileSearchOpen((current) => !current)}
                  >
                    <Search className={MOBILE_ICON_SIZE} aria-hidden />
                  </button>
                </span>
                <span title="Notifications" className="lg:hidden">
                  <Link
                    to="/notifications"
                    aria-label="Notifications"
                    className={clsx(MOBILE_HEADER_ICON_CLASS, notificationsActive ? 'bg-zinc-900 text-white' : undefined)}
                  >
                    <Bell className={mobileNotificationBellClass} />
                  </Link>
                </span>
                {MOBILE_HELP_NAV.map((n) => {
                  if ('href' in n) {
                    return (
                      <MobileHeaderNavItem
                        key={n.href}
                        href={n.href}
                        label={n.label}
                        icon={n.icon}
                        active={false}
                      />
                    );
                  }

                  return (
                    <MobileHeaderNavItem
                      key={n.to}
                      to={n.to}
                      label={n.label}
                      icon={n.icon}
                      active={false}
                    />
                  );
                })}
                <span title="Notifications" className="hidden lg:inline-flex">
                  <Link to="/notifications">
                    <Button
                      variant="ghost"
                      aria-label="Notifications"
                      className={notificationButtonClass}
                    >
                      <Bell className={notificationBellClass} />
                    </Button>
                  </Link>
                </span>
                {user ? (
                  <>
                    <span title={signedInName} className="hidden lg:inline-flex">
                      <Button
                        variant="ghost"
                        aria-label={`Profile: ${signedInName}`}
                        className="max-w-[220px] gap-2"
                        disabled
                      >
                        <User className="h-5 w-5 shrink-0" />
                        <span className="truncate text-xs text-zinc-600">{signedInName}</span>
                      </Button>
                    </span>
                  </>
                ) : (
                  <span title="Profile" className="hidden lg:inline-flex">
                    <Button variant="ghost" aria-label="Profile" disabled>
                      <User className="h-5 w-5" />
                    </Button>
                  </span>
                )}
                {user ? (
                  <div className="hidden items-center gap-2 lg:flex">
                    <span title="Sign out">
                      <Button variant="ghost" aria-label="Sign out" onClick={() => void logout()}>
                        <LogOut className="h-5 w-5" aria-hidden />
                      </Button>
                    </span>
                  </div>
                ) : null}
                {user ? (
                  <span title="Sign out" className="lg:hidden">
                    <button
                      type="button"
                      className={MOBILE_HEADER_ICON_CLASS}
                      aria-label="Sign out"
                      onClick={() => void logout()}
                    >
                      <LogOut className={MOBILE_ICON_SIZE} aria-hidden />
                    </button>
                  </span>
                ) : null}
              </div>
            </div>

            <div
              className="grid grid-cols-2 gap-2 border-t border-zinc-200/80 px-3 pb-3 pt-3 lg:hidden"
            >
              {MOBILE_PRIMARY_NAV.map((n) => {
                if ('href' in n) {
                  return (
                    <MobilePrimaryNavItem
                      key={n.href}
                      href={n.href}
                      label={n.label}
                      icon={n.icon}
                      active={false}
                    />
                  );
                }

                const active = isNavActive(activePath, n);

                return (
                  <MobilePrimaryNavItem
                    key={n.to}
                    to={n.to}
                    label={n.label}
                    icon={n.icon}
                    active={active}
                    onClick={handleNavClick(n)}
                  />
                );
              })}
            </div>

            {mobileSearchOpen ? (
              <div id="mobile-header-search" className="border-t border-zinc-200/80 px-3 pb-3 pt-3 lg:hidden">
                <HeaderSearchForm value={searchValue} onChange={setSearchValue} onSubmit={submitSearch} />
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <div className="mx-auto flex min-h-screen max-w-[1400px] flex-col px-1 pb-3 md:px-4 md:pb-4">
        <main className="min-w-0 flex-1 py-2 md:py-4">{props.children}</main>
      </div>
    </div>
  );
}
